import { createStore } from 'zustand';

import { isProductionRuntime } from '@/lib/runtime-flags';
import type { Tweet } from '@/types/tweet/tweet';

const INITIAL_VISIBLE_COUNT = 10;

interface ExpandedConversationState {
  anchorTweet: Tweet;
  visibleCount: number;
}

export // eslint-disable-next-line no-unused-vars
interface ConversationSurfaceState {
  clearPrefetchedConversation: (conversationTweetId: string) => void;
  closeConversation: () => void;
  expandedConversation: ExpandedConversationState | null;
  markPrefetchStarted: (conversationTweetId: string) => boolean;
  openConversation: (tweet: Tweet) => void;
  prefetchConversation: (tweet: Tweet) => void;
  prefetchedConversationIds: ReadonlySet<string>;
  reset: () => void;
  setPrefetchConversation: (function_: ((tweet: Tweet) => void) | undefined) => void;
  showMoreConversation: (anchorTweetId: string) => void;
}

const noopPrefetch: (tweet: Tweet) => void = () => {
  // intentionally empty; default prefetch handler when no subscriber overrides it
};

const initialState = (): Pick<
  ConversationSurfaceState,
  'expandedConversation' | 'prefetchedConversationIds' | 'prefetchConversation'
> => ({
  expandedConversation: null,
  prefetchedConversationIds: new Set<string>(),
  prefetchConversation: noopPrefetch,
});

export const createConversationSurfaceStore = () =>
  createStore<ConversationSurfaceState>((set, get) => ({
    ...initialState(),
    openConversation: (tweet) => {
      set((state) => {
        const current = state.expandedConversation;
        if (current?.anchorTweet.id === tweet.id) {
          return { expandedConversation: null };
        }
        return {
          expandedConversation: {
            anchorTweet: tweet,
            visibleCount: INITIAL_VISIBLE_COUNT,
          },
        };
      });
    },
    closeConversation: () => {
      if (!get().expandedConversation) {
        return;
      }
      set({ expandedConversation: null });
    },
    showMoreConversation: (anchorTweetId) => {
      set((state) => {
        const current = state.expandedConversation;
        if (!current || current.anchorTweet.id !== anchorTweetId) {
          return state;
        }
        return {
          expandedConversation: {
            anchorTweet: current.anchorTweet,
            visibleCount: current.visibleCount + INITIAL_VISIBLE_COUNT,
          },
        };
      });
    },
    markPrefetchStarted: (conversationTweetId) => {
      // Guard against duplicate prefetches by tracking which canonical IDs are in-flight.
      const cache = get().prefetchedConversationIds;
      if (cache.has(conversationTweetId)) {
        return false;
      }
      const nextCache = new Set(cache);
      nextCache.add(conversationTweetId);
      set({ prefetchedConversationIds: nextCache });
      return true;
    },
    clearPrefetchedConversation: (conversationTweetId) => {
      const cache = get().prefetchedConversationIds;
      if (!cache.has(conversationTweetId)) {
        return;
      }
      const nextCache = new Set(cache);
      nextCache.delete(conversationTweetId);
      set({ prefetchedConversationIds: nextCache });
    },
    setPrefetchConversation: (function_) => {
      if (function_) {
        set({ prefetchConversation: function_ });
        return;
      }
      set(() => ({ prefetchConversation: noopPrefetch }));
    },
    reset: () => {
      set(initialState());
    },
  }));

export type ConversationSurfaceStore = ReturnType<typeof createConversationSurfaceStore>;

export const conversationSurfaceStore = createConversationSurfaceStore();

let activeControllers = 0;

// Retain resets when the last subscriber leaves so a new surface mounts with the
// default snapshot (mirrors provider-unmount behavior from the legacy context).
export function retainConversationSurfaceStore(): () => void {
  activeControllers += 1;
  return () => {
    activeControllers = Math.max(0, activeControllers - 1);
    if (activeControllers === 0) {
      conversationSurfaceStore.getState().reset();
    }
  };
}

export function resetConversationSurfaceStore(): void {
  conversationSurfaceStore.getState().reset();
}

declare global {
  interface Window {
    __SWEETISTICS_CONVERSATION_STORES__?: Set<ConversationSurfaceStore>;
  }
}

if (!isProductionRuntime() && globalThis.window !== undefined) {
  const registry = globalThis.window?.__SWEETISTICS_CONVERSATION_STORES__ ?? new Set<ConversationSurfaceStore>();
  if (!registry.has(conversationSurfaceStore)) {
    registry.add(conversationSurfaceStore);
  }
  globalThis.window.__SWEETISTICS_CONVERSATION_STORES__ = registry;
}
